import { clearToken, clearTokenAdmin } from "App/Authentification/Store/actions";
import { INITIAL_STATE } from "App/Authentification/Store/constants";
import { selectTokenAdminValue, selectTokenValue } from "App/Authentification/Store/selectors";
import { TAB_MAP } from "App/Constants";
import { hideLoading, showLoading } from "App/Loading/Store/actions";
import { showError } from "App/Toast/Toast";
import PropTypes from "prop-types";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  errorDataResource,
  razAllDataResource,
  razDataResource,
  setDataResource,
  stateDataResource
} from "../Store/actions";
import {
  DATA_CREATED,
  DATA_DELETED,
  DATA_NO_STATE,
  DATA_UPDATED
} from "../Store/constants";
import {
  DELETE,
  GET,
  PATCH,
  POST,
  PUT,
  UNAUTHORIZED,
  createResource,
  deleteResource,
  getResource,
  getResourceWithParams,
  getResources,
  getResourcesWithParams,
  updateResource
} from "./RequestUtils";

// #region CONSTANTES
export const GET_LIST = 0;
export const GET_LIST_WITH_PARAMS = 1;
export const GET_SINGLE = 2;
export const GET_SINGLE_WITH_PARAMS = 3;
const updateCommand = [POST, PATCH, DELETE];
// #endregion

// #region HOOK
/**
 * HOOK permettant de gérer les requêtes de type REST avec des données en JSON
 * @class
 * @category Data
 * @param {useJsonRequest.propTypes} props Les propriétés du composant
 */
function useJsonRequest(props) {
  // #region INITIALISATION
  // Récupération et initialisation (les propTypes ne font rien pour les HOOKs) des props
  const {
    command,
    getMode = null,
    manageToken = true,
    razScreenFunc = null,
    resource,
    silent = false,
    timeout = 30000,
    tokenObligatory = true,
    tokenAdminObligatory = false,
    url
  } = props;

  // Récupération des sélecteurs
  const token = useSelector((state) => selectTokenValue(state));
  const tokenAdmin = useSelector((state) => selectTokenAdminValue(state));

  // Récupération du dispatch
  const dispatch = useDispatch();

  // Récupération de l'historique
  const history = useHistory();

  // Traduction i18n
  const { t } = useTranslation();
  // #endregion

  // #region UTILS
  /**
   * Gestion du lancement du chargement
   * @private
   * @returns true si la requête peut être envoyée, false sinon
   */
  function beginLoading() {
    if (!silent) {
      // Affichage du composant de chargement
      dispatch(showLoading());
    }
  };

  /**
   * Gestion de la fin de chargement
   * @private
   */
  function endLoading() {
    if (silent) {
      return;
    }

    // On cache le composant de chargement
    dispatch(hideLoading());
  };

  /**
   * Nom de la ressource pour la sauvegarde dans le store
   * @param {string} suffixResource Suffixe de la resource afin de différencier le résultat dans le store si le nom de la ressource est identique pour plusieurs ws ayanyt des différences dans les paramètres de l'url
   * @private
   */
  function getStoreResourceName(suffixResource) {
    let storeResourceName = resource;
    if (suffixResource) {
      storeResourceName = storeResourceName.concat("-", suffixResource);
    }
    return storeResourceName;
  };
  // #endregion

  // #region CALLBACK
  /**
   * Callback lors du succès d'une requête
   * @private
   * @param {objet} result Résultat de la requête
   */
  function onSuccess(result, suffixResource) {
    // Détermination du nom de l'objet dans le store
    let storeResourceName = getStoreResourceName(suffixResource);

    // Notification des données reçues
    if (result) {
      dispatch(setDataResource(storeResourceName, result));
    }

    // Notification du résultat
    switch (command) {
      case DELETE:
        dispatch(stateDataResource(storeResourceName, DATA_DELETED, result));
        break;

      case GET:
        if (result) {
          dispatch(setDataResource(storeResourceName, result));
        }
        break;

      case POST:
        dispatch(stateDataResource(storeResourceName, DATA_CREATED, result));
        break;

      case PUT:
      case PATCH:
        dispatch(stateDataResource(storeResourceName, DATA_UPDATED, result));
        break;

      default:
        break;
    }

    if (updateCommand.indexOf(command) !== -1) {
      setTimeout(() => {
        dispatch(stateDataResource(storeResourceName, DATA_NO_STATE, null));
      }, 500);
      setTimeout(() => {
        dispatch(razDataResource(storeResourceName));
      }, 500);
    }

    // Arrêt du chargement
    endLoading();
  };

  /**
   * Callback lors de l'échec d'une requête
   * @private
   * @param {objet} error Contient les informations sur l'erreur
   */
  function onError(error, suffixResource) {
    // Détermination du nom de l'objet dans le store
    let storeResourceName = getStoreResourceName(suffixResource);

    // RAZ des anciennes données
    dispatch(razDataResource(storeResourceName));

    if (error.status === UNAUTHORIZED) {
      // RAZ du store si non autorisé
      dispatch(razAllDataResource());
      // Si la requête utilisait le token admin, on l'invalide pour forcer son renouvellement
      if (tokenAdminObligatory) {
        dispatch(clearTokenAdmin(INITIAL_STATE.tokenAdmin));
      }
      // Redirection vers accueil onglet carte si token JWT expiré (depuis n'importe quelle page ou depuis accueil onglet Ma distribution)
      const isExpiredJwt = error.error === "TRANSLATION_KEY:login.tokenExpired" ||
        (typeof error.error === "string" && error.error.includes("tokenExpired"));
      if (isExpiredJwt) {
        dispatch(clearToken(INITIAL_STATE.token));
        dispatch(clearTokenAdmin(INITIAL_STATE.tokenAdmin));
        history.push({ pathname: "/", state: { selectedIndexTab: TAB_MAP } });
      }
    }

    // Affichage d'un message si error est une chaine (sauf token expiré : renouvellement en arrière-plan, l'utilisateur ne voit rien)
    const isTokenExpiredSilent = error.status === UNAUTHORIZED && tokenAdminObligatory;
    if (typeof error.error === "string" && !silent && !isTokenExpiredSilent) {
      let errorMessage = error.error;
      let translationKey = null;
      if (errorMessage.startsWith("TRANSLATION_KEY:")) {
        translationKey = errorMessage.replace("TRANSLATION_KEY:", "");
        errorMessage = t(translationKey);
      }
      const loginOverlayKeys = [
        "login.badPassword", "login.wrongPassword", "login.mailIncorrect", "login.mailPasswordRequired",
        "login.emailNotSame", "login.passwordNotSame", "login.passwordForceTooLow", "login.emailNotFound",
        "login.mailOrPasswordIncorrect", "login.accountAlreadyExists", "login.accountNotFound"
      ];
      const paymentOverlayKeys = [
        "achat.errorRefused", "achat.errorCancelled", "achat.errorAborted", "achat.errorExpired", "achat.error"
      ];
      const isLoginOverlay = translationKey && loginOverlayKeys.includes(translationKey);
      const isPaymentOverlay = translationKey && paymentOverlayKeys.includes(translationKey);
      const useOverlay = isLoginOverlay || isPaymentOverlay;
      const overlayTitle = isLoginOverlay ? t("login.connection") : (isPaymentOverlay ? "Paiement" : null);
      showError(errorMessage, useOverlay ? { useOverlay: true, title: overlayTitle } : undefined);
    }

    // Arrêt du chargement
    endLoading();

    // Notification de l'erreur
    dispatch(errorDataResource(storeResourceName, error));
    // RAZ de l'erreur après 500ms
    setTimeout(() => {
      dispatch(errorDataResource(storeResourceName, null));
    }, 500);
  };

  /**
    * Callback lors de l'échec d'une requête
    * @private
    * @param {objet} error Contient les informations sur l'erreur
    */
  function onDisconnect() {
    // RAZ des anciennes données
    dispatch(razDataResource());

    // Navigation vers la carte
    history.push({ pathname: "/" });
  };


  // #endregion

  // #region FONCTION DE RETOUR => la requête a exécutée
  // Détermination de la fonction de retour
  let result = () => { };
  // Gestion du token ?
  let tokenValue = (manageToken) ? token : null;

  switch (command) {
    case GET:
      switch (getMode) {
        case GET_LIST:
          // Récupération d'une liste de ressource
          result = (suffixResource = "") => {
            // Si pas de token admin mais obligatoire, on sort
            if (tokenAdminObligatory && !tokenAdmin) {
              onDisconnect()
              return;
            }

            // Si pas de token mais token obligatoire, on sort
            if (tokenObligatory && !token) {
              onDisconnect()
              return;
            }

            // RAZ de l'écran avant récupération des données
            if (razScreenFunc) {
              razScreenFunc();
            }

            // Début du chargement
            beginLoading();

            // RAZ des anciennes données
            dispatch(razDataResource(getStoreResourceName(suffixResource)));

            // Requête de récupération de ressources
            getResources(url, tokenValue, resource, timeout)
              .then((result) => onSuccess(result, suffixResource))
              .catch((error) => onError(error, suffixResource));
          };
          break;

        case GET_LIST_WITH_PARAMS:
          // Récupération d'une liste de ressource avec paramètres
          result = (params, suffixResource = "") => { // params est un tableau de chaine de la forme [cle1=val1, cle2=val2, ...]
            // Si pas de token mais token obligatoire, on sort
            if (tokenObligatory && !token) {
              onDisconnect()
              return;
            }

            // RAZ de l'écran avant récupération des données
            if (razScreenFunc) {
              razScreenFunc();
            }

            // Début du chargement
            beginLoading();

            // RAZ des anciennes données
            dispatch(razDataResource(getStoreResourceName(suffixResource)));

            // Requête de récupération de ressources avec paramètres
            getResourcesWithParams(url, tokenAdminObligatory ? tokenAdmin : tokenValue, resource, params, timeout)
              .then((result) => onSuccess(result, suffixResource))
              .catch((error) => onError(error, suffixResource));
          };
          break;

        case GET_SINGLE:
          // Récupération d'une ressource
          result = (id, suffixResource = "") => {
            // Si pas de token mais token obligatoire, on sort
            if (tokenObligatory && !token) {
              onDisconnect()
              return;
            }

            // RAZ de l'écran avant récupération des données
            if (razScreenFunc) {
              razScreenFunc();
            }

            // Début du chargement
            beginLoading();

            // RAZ des anciennes données
            dispatch(razDataResource(getStoreResourceName(suffixResource)));

            // Requête de récupération d'une ressource
            getResource(url, tokenAdminObligatory ? tokenAdmin : tokenValue, resource, id, timeout)
              .then((result) => onSuccess(result, suffixResource))
              .catch((error) => onError(error, suffixResource));
          };
          break;

        case GET_SINGLE_WITH_PARAMS:
          // Récupération d'une ressource
          result = (id, params, suffixResource = "") => {
            // Si pas de token mais token obligatoire, on sort
            if (tokenObligatory && !token) {
              onDisconnect()
              return;
            }

            // RAZ de l'écran avant récupération des données
            if (razScreenFunc) {
              razScreenFunc();
            }

            // Début du chargement
            beginLoading();

            // RAZ des anciennes données
            dispatch(razDataResource(getStoreResourceName(suffixResource)));

            // Requête de récupération d'une ressource
            getResourceWithParams(url, tokenValue, resource, id, params, timeout)
              .then((result) => onSuccess(result, suffixResource))
              .catch((error) => onError(error, suffixResource));
          };
          break;

        default:
          break;
      }
      break;

    case POST:
      result = (data, suffixResource = "") => {
        // Si pas de token mais token obligatoire, on sort
        if (tokenObligatory && !token) {
          onDisconnect()
          return;
        }
        // Début du chargement
        beginLoading();

        // Requête de création d'une ressource
        createResource(url, tokenAdminObligatory ? tokenAdmin : token, resource, data)
          .then((result) => onSuccess(result, suffixResource))
          .catch((error) => onError(error, suffixResource));
      };
      break;

    case PUT:
    case PATCH:
      result = (id, data, suffixResource = "") => {
        // Si pas de token admin mais obligatoire, on sort
        if (tokenAdminObligatory && !tokenAdmin) {
          onDisconnect()
          return;
        }

        // Début du chargement
        beginLoading();

        // Requête de mise à jours d'une ressource
        updateResource(url, tokenAdminObligatory ? tokenAdmin : token, resource, id, data)
          .then((result) => onSuccess(result, suffixResource))
          .catch((error) => onError(error, suffixResource));
      };
      break;

    case DELETE:
      result = (id, suffixResource = "") => {
        // Si pas de token mais token obligatoire, on sort
        if (tokenObligatory && !token) {
          onDisconnect()
          return;
        }

        // Début du chargement
        beginLoading();

        // Requête de suppression d'une ressource
        deleteResource(url, tokenValue, resource, id)
          .then(() => onSuccess(null, suffixResource))
          .catch((error) => onError(error, suffixResource));
      };
      break;

    default:
      break;
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(result, [token]);
  // #endregion
}
// #endregion

// #region PROPRIETES
/**
  * Type des propriétés
  * @typedef {Object} useJsonRequest.propTypes
  * @property {string} url URL de la requête
  * @property {string} resource Nom de la resource pour la requête
  * @property {string} command Commade HTTP (GET, POST, PATCH ou DELETE)
  * @property {number} [getMode=null] Mode de la requête de type GET : 0=requête sans paramètres et sans id, 1=requête avec paramètres, 2=requête avec id
  * @property {boolean} [manageToken=true] false pour ne pas gérer le header Authorization dans les requêtes, true sinon
  * @property {function} [razScreenFunc=null] Fonction permettant de RAZ l'écran avant l'exécution de la requête
  * @property {boolean} [silent=false] true pour ne pas gérer le composant de chargement, false sinon
  * @property {boolean} [tokenObligatory=true] true si le token est obligatoire, false sinon
  * @property {boolean} [tokenAdminObligatory=false] true si le token est obligatoire, false sinon
  */
useJsonRequest.propTypes = {
  command: PropTypes.string.isRequired,
  getMode: PropTypes.number,
  manageToken: PropTypes.bool,
  razScreenFunc: PropTypes.func,
  resource: PropTypes.string.isRequired,
  silent: PropTypes.bool,
  tokenObligatory: PropTypes.bool,
  tokenAdminObligatory: PropTypes.bool,
  url: PropTypes.string.isRequired,
};

/*
  Valeurs par défaut des propriétés
*/
useJsonRequest.defaultProps = {
  getMode: null,
  manageToken: true,
  razScreenFunc: null,
  silent: false,
  tokenObligatory: true,
  tokenAdminObligatory: false
};
// #endregion

export { useJsonRequest };

